/**
 * Refresh Control Component
 * Controls for auto-refresh interval and manual refresh
 */

import { Button } from '@/components/ui/Button';
import { RefreshCw, Play, Pause } from 'lucide-react';
import { useI18n } from '@/i18n';

export interface RefreshControlProps {
  /**
   * Current refresh interval in milliseconds (0 = disabled)
   */
  interval: number;
  /**
   * Whether auto-refresh is enabled
   */
  enabled: boolean;
  /**
   * Whether a refresh is currently in progress
   */
  isRefreshing?: boolean;
  /**
   * Callback when interval changes
   */
  onIntervalChange: (interval: number) => void;
  /**
   * Callback when enabled state changes
   */
  onEnabledChange: (enabled: boolean) => void;
  /**
   * Callback for manual refresh
   */
  onRefresh: () => void;
}

export function RefreshControl({
  interval,
  enabled,
  isRefreshing = false,
  onIntervalChange,
  onEnabledChange,
  onRefresh,
}: RefreshControlProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.refresh;

  const REFRESH_INTERVALS = [
    { value: 0, label: t.manual },
    { value: 5000, label: '5s' },
    { value: 10000, label: '10s' },
    { value: 30000, label: '30s' },
    { value: 60000, label: '1m' },
  ];

  const handleIntervalChange = (newInterval: number) => {
    onIntervalChange(newInterval);
    // Auto-enable if selecting an interval, auto-disable if selecting manual
    if (newInterval > 0) {
      onEnabledChange(true);
    } else {
      onEnabledChange(false);
    }
  };

  return (
    <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-2">
      {/* Auto-refresh toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">{t.autoRefresh}:</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEnabledChange(!enabled)}
          disabled={interval === 0}
          className="px-2"
          title={enabled ? t.autoRefresh : t.autoRefresh}
        >
          {enabled ? (
            <Pause className="w-4 h-4 text-blue-600" />
          ) : (
            <Play className="w-4 h-4 text-gray-400" />
          )}
        </Button>
      </div>

      {/* Interval selector */}
      <div className="flex items-center gap-2">
        <label htmlFor="refresh-interval" className="text-sm font-medium text-gray-700">
          {t.interval}:
        </label>
        <select
          id="refresh-interval"
          value={interval}
          onChange={(e) => handleIntervalChange(Number(e.target.value))}
          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {REFRESH_INTERVALS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Manual refresh button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="ml-2"
      >
        <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? t.refreshing : t.refreshButton}
      </Button>
    </div>
  );
}
